import React, { useState, useEffect, useRef } from "react";
import { UserNavbar } from "../components/Navbar";
import { masterAPI, productionAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/user-dashboard.css";

// Type definitions
interface Publication {
  id: number;
  name: string;
  code: string;
  publication_type?: string; // VK, OSP, TOI
  location?: string; // ✅ Added location field
}

interface Machine {
  id: number;
  name: string;
  code: string;
}

interface Tower {
  id: number;
  name: string;
  code: string;
}

interface DowntimeReason {
  id: number;
  reason: string;
  name?: string;
  code?: string;
}

interface NewsprintType {
  id: number;
  name: string;
  code: string;
}

interface NewsprintWasteCategory {
  id: number;
  name: string;
  code: string;
  category_type: string;
  description?: string;
}

interface PlatesWasteCategory {
  id: number;
  name: string;
  code: string;
  category_type: string;
  description?: string;
}

interface WasteEntry {
  id?: number;
  category_id: number | string;
  quantity: number | string;
  weight_kgs?: number | string; // For newsprint only
  remarks: string;
}

interface ProductionRecord {
  id: number;
  user_id: number;
  publication_id: number | null;
  custom_publication_name: string | null;
  po_number: number;
  color_pages: number;
  bw_pages: number;
  total_pages: number;
  machine_id: number;
  lprs_time: string;
  page_start_time: string;
  page_end_time: string;
  downtime_entries: Array<{
    downtime_reason_id: number;
    downtime_duration: string;
  }>;
  tower_ids?: number[];
  newsprint_waste_entries?: Array<{
    newsprint_waste_category_id: number;
    waste_quantity: number;
    waste_kgs: number;
    remarks: string | null;
  }>;
  plates_waste_entries?: Array<{
    plates_waste_category_id: number;
    waste_quantity: number;
    remarks: string | null;
  }>;
  newsprint_id: number | null;
  newsprint_kgs: number;
  plate_consumption: number;
  remarks: string;
  record_date: string;
  created_at: string;
}

interface LprsSchedule {
  publication_id: number;
  publication_name: string;
  publication_type: string;
  location?: string;
  scheduled_lprs_time: string;
}

// Separate type for edit form data with string IDs from form inputs
interface EditFormData {
  id: number;
  user_id: number;
  publication_id: number | null;
  custom_publication_name: string | null | undefined;
  po_number: number | string;
  color_pages: number;
  bw_pages: number;
  total_pages: number;
  machine_id: number | string;
  lprs_time: string;
  page_start_time: string;
  page_end_time: string;
  downtime_entries: Array<{
    downtime_reason_id: string | number;
    downtime_duration: string;
  }>;
  tower_ids: number[];
  newsprint_waste_entries: Array<{
    newsprint_waste_category_id: number | string;
    waste_quantity: number | string;
    waste_kgs: number | string;
    remarks: string;
  }>;
  plates_waste_entries: Array<{
    plates_waste_category_id: number | string;
    waste_quantity: number | string;
    remarks: string;
  }>;
  newsprint_id: number | string | null;
  newsprint_kgs: number | string;
  plate_consumption: number | string;
  remarks: string;
  record_date: string;
  created_at: string;
}

// API payload type
interface ProductionRecordPayload {
  id?: number;
  user_id: number;
  publication_id: number | null;
  custom_publication_name?: string;
  po_number: number;
  color_pages: number;
  bw_pages: number;
  total_pages: number;
  machine_id: number;
  lprs_time: string;
  page_start_time: string;
  page_end_time: string;
  downtime_entries: Array<{
    downtime_reason_id: number;
    downtime_duration: string;
  }>;
  tower_ids: number[];
  newsprint_waste_entries?: Array<{
    newsprint_waste_category_id: number;
    waste_quantity: number;
    waste_kgs: number;
    remarks: string;
  }>;
  plates_waste_entries?: Array<{
    plates_waste_category_id: number;
    waste_quantity: number;
    remarks: string;
  }>;
  newsprint_id: number | null;
  newsprint_kgs: number;
  plate_consumption: number;
  remarks: string;
  record_date: string;
}

type TabType = "form" | "reports";
type ModalType = "view" | "edit" | null;

const isMonthEndDate = (dateValue: string): boolean => {
  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) return false;

  return day === new Date(year, month, 0).getDate();
};

const formatDateForInput = (dateValue?: string | null): string => {
  if (!dateValue) return "";
  const rawValue = String(dateValue);
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) return rawValue;
  return rawValue.split("T")[0].split(" ")[0] || "";
};

const formatDateForDisplay = (dateValue?: string | null): string => {
  const inputDate = formatDateForInput(dateValue);
  if (!inputDate) return "N/A";
  const [year, month, day] = inputDate.split("-").map(Number);
  if (!year || !month || !day) return inputDate;
  return new Date(year, month - 1, day).toLocaleDateString();
};

const timeToSeconds = (value?: string | null): number | null => {
  if (!value) return null;
  const [hours, minutes, seconds = "0"] = String(value).split(":");
  const parsedHours = parseInt(hours, 10);
  const parsedMinutes = parseInt(minutes, 10);
  const parsedSeconds = parseInt(seconds, 10);

  if ([parsedHours, parsedMinutes, parsedSeconds].some((part) => Number.isNaN(part))) {
    return null;
  }

  return parsedHours * 3600 + parsedMinutes * 60 + parsedSeconds;
};

const formatClockTime = (value?: string | null): string => {
  const seconds = timeToSeconds(value);
  if (seconds === null) return "";
  const date = new Date(2000, 0, 1, Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60);
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatTimeDifference = (seconds: number): string => {
  const absSeconds = Math.abs(seconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const remainingSeconds = absSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const getSignedClockDifferenceSeconds = (
  actualSeconds: number,
  scheduledSeconds: number,
): number => {
  const daySeconds = 24 * 60 * 60;
  const halfDaySeconds = daySeconds / 2;
  let differenceSeconds = actualSeconds - scheduledSeconds;

  if (differenceSeconds > halfDaySeconds) {
    differenceSeconds -= daySeconds;
  } else if (differenceSeconds < -halfDaySeconds) {
    differenceSeconds += daySeconds;
  }

  return differenceSeconds;
};

export const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [towers, setTowers] = useState<Tower[]>([]);
  const [downtimeReasons, setDowntimeReasons] = useState<DowntimeReason[]>([]);
  const [newsprintTypes, setNewsprintTypes] = useState<NewsprintType[]>([]);
  const [newsprintWasteCategories, setNewsprintWasteCategories] = useState<
    NewsprintWasteCategory[]
  >([]);
  const [platesWasteCategories, setPlatesWasteCategories] = useState<
    PlatesWasteCategory[]
  >([]);
  const [userRecords, setUserRecords] = useState<ProductionRecord[]>([]);
  const [activeLprsSchedules, setActiveLprsSchedules] = useState<LprsSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Publication search states
  const [publicationSearch, setPublicationSearch] = useState("");
  const [filteredPublications, setFilteredPublications] = useState<
    Publication[]
  >([]);
  const [showPublicationDropdown, setShowPublicationDropdown] = useState(false);
  const publicationDropdownRef = useRef<HTMLDivElement>(null);

  // ✅ Publications filtered by user's location
  const [vkPublications, setVkPublications] = useState<Publication[]>([]);
  const [ospPublications, setOspPublications] = useState<Publication[]>([]);
  const [toiPublications, setToiPublications] = useState<Publication[]>([]);
  const [publicationType, setPublicationType] = useState<
    "VK" | "OSP" | "TOI" | ""
  >("");
  const [allPublications, setAllPublications] = useState<Publication[]>([]);

  const [formData, setFormData] = useState({
    publication_id: "",
    custom_publication_name: "",
    po_number: "",
    color_pages: 0,
    bw_pages: 0,
    total_pages: 0,
    machine_id: "",
    tower_ids: [] as number[],
    lprs_time: "00:00:00",
    page_start_time: "00:00:00",
    page_end_time: "00:00:00",
    downtime_entries: [
      {
        downtime_reason_id: "",
        downtime_duration: "00:00:00",
      },
    ],
    newsprint_id: "",
    newsprint_kgs: 0,
    plate_consumption: 0,
    ink_c: 0,
    ink_m: 0,
    ink_y: 0,
    ink_k: 0,
    newsprint_waste_entries: [
      {
        category_id: "",
        quantity: 0,
        weight_kgs: 0,
        remarks: "",
      },
    ],
    plates_waste_entries: [
      {
        category_id: "",
        quantity: 0,
        remarks: "",
      },
    ],
    remarks: "",
    record_date: new Date().toISOString().split("T")[0],
  });

  // Modal states
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedRecord, setSelectedRecord] = useState<ProductionRecord | null>(
    null,
  );
  const [editFormData, setEditFormData] = useState<EditFormData | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("form");
  const supportsMonthlyInk = publicationType === "VK" || publicationType === "OSP";
  const isInkEntryEnabled = supportsMonthlyInk && isMonthEndDate(formData.record_date);

  // ✅ Load master data and filter by user location
  useEffect(() => {
    const loadMasterData = async (): Promise<void> => {
      try {
        console.log(`📍 Loading publications for location: ${user?.location}`);

        const [
          vkRes,
          ospRes,
          toiRes,
          machRes,
          towerRes,
          dtRes,
          npRes,
          newsprintWasteRes,
          platesWasteRes,
          lprsSchedulesRes,
        ] = await Promise.all([
          masterAPI.getPublications("VK"),
          masterAPI.getPublications("OSP"),
          masterAPI.getPublications("TOI"),
          masterAPI.getMachines(),
          masterAPI.getTowers(),
          masterAPI.getDowntimeReasons(),
          masterAPI.getNewsprintTypes(),
          masterAPI.getNewsprintWasteCategories(),
          masterAPI.getPlatesWasteCategories(),
          productionAPI.getActiveLprsScheduledTimes(),
        ]);

        const vkData = vkRes.data.data || vkRes.data || [];
        const ospData = ospRes.data.data || ospRes.data || [];
        const toiData = toiRes.data.data || toiRes.data || [];
        const machinesData = machRes.data.data || machRes.data || [];
        const towersData = towerRes.data.data || towerRes.data || [];
        const downtimeReasonsData = dtRes.data.data || dtRes.data || [];
        const newsprintTypesData = npRes.data.data || npRes.data || [];
        const newsprintWasteCategoriesData =
          newsprintWasteRes.data.data || newsprintWasteRes.data || [];
        const platesWasteCategoriesData =
          platesWasteRes.data.data || platesWasteRes.data || [];
        const lprsSchedulesData =
          lprsSchedulesRes.data.data || lprsSchedulesRes.data || [];

        console.log("📊 Raw API Response - VK:", vkData.length);
        console.log("📊 Raw API Response - OSP:", ospData.length);
        console.log("📊 Raw API Response - TOI:", toiData.length);
        console.log("📊 Sample VK publication:", vkData[0]);
        console.log("📊 Sample OSP publication:", ospData[0]);

        // ✅ Filter publications by user's location (case-insensitive)
        const filterByLocation = (pubs: Publication[]): Publication[] => {
          if (!Array.isArray(pubs)) return [];
          const filtered = pubs.filter(
            (pub) =>
              pub.location?.toLowerCase() === user?.location?.toLowerCase(),
          );
          console.log(
            `🔍 Filtered ${pubs.length} publications to ${filtered.length} for location: ${user?.location}`,
          );
          return filtered;
        };

        const vkFiltered = filterByLocation(vkData);
        const ospFiltered = filterByLocation(ospData);
        const toiFiltered = filterByLocation(toiData);

        setVkPublications(vkFiltered);
        setOspPublications(ospFiltered);
        setToiPublications(toiFiltered);

        // ✅ Combine all publications for search
        const combined = [...vkFiltered, ...ospFiltered, ...toiFiltered];
        setAllPublications(combined);

        setMachines(Array.isArray(machinesData) ? machinesData : []);
        setTowers(Array.isArray(towersData) ? towersData : []);
        setDowntimeReasons(
          Array.isArray(downtimeReasonsData) ? downtimeReasonsData : [],
        );
        setNewsprintTypes(
          Array.isArray(newsprintTypesData) ? newsprintTypesData : [],
        );
        setNewsprintWasteCategories(
          Array.isArray(newsprintWasteCategoriesData)
            ? newsprintWasteCategoriesData
            : [],
        );
        setPlatesWasteCategories(
          Array.isArray(platesWasteCategoriesData)
            ? platesWasteCategoriesData
            : [],
        );
        setActiveLprsSchedules(
          Array.isArray(lprsSchedulesData) ? lprsSchedulesData : [],
        );

        console.log("✅ Publications filtered by location:", {
          location: user?.location,
          vk: vkFiltered.length,
          osp: ospFiltered.length,
          namma: toiFiltered.length,
          total: combined.length,
        });

        if (combined.length === 0) {
          setErrorMessage(
            `⚠️ No publications available for your location: ${user?.location}`,
          );
        } else {
          setErrorMessage(""); // Clear error if publications are found
        }
      } catch (error) {
        console.error("❌ Error loading master data:", error);
        setErrorMessage("Failed to load master data");
      } finally {
        setIsLoading(false);
      }
    };

    loadMasterData();
  }, [user?.location]);

  // Load user records
  useEffect(() => {
    const loadUserRecords = async (): Promise<void> => {
      if (!user) return;
      try {
        const response = await productionAPI.getRecordsByUser(user.id);
        const records = response.data?.data || response.data || [];
        setUserRecords(Array.isArray(records) ? records : []);
      } catch (error) {
        console.error("Error loading user records:", error);
        setErrorMessage("Failed to load production records");
      }
    };

    loadUserRecords();
  }, [user]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        publicationDropdownRef.current &&
        !publicationDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPublicationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Handle publication type change - show filtered publications
  const handlePublicationTypeChange = (type: "VK" | "OSP" | "TOI") => {
    setPublicationType(type);
    setPublicationSearch("");

    let pubs: Publication[] = [];
    if (type === "VK") {
      pubs = vkPublications;
    } else if (type === "OSP") {
      pubs = ospPublications;
    } else if (type === "TOI") {
      pubs = toiPublications;
    }

    setFilteredPublications(pubs);
    setFormData((prev) => ({
      ...prev,
      publication_id: "",
      custom_publication_name: "",
      ...(type === "VK" || type === "OSP"
        ? {}
        : {
            ink_c: 0,
            ink_m: 0,
            ink_y: 0,
            ink_k: 0,
          }),
    }));
  };

  // Handle publication search
  const handlePublicationSearch = (value: string): void => {
    setPublicationSearch(value);
    setShowPublicationDropdown(true);

    // Filter based on selected type
    let pubsToSearch: Publication[] = [];
    if (publicationType === "VK") {
      pubsToSearch = vkPublications;
    } else if (publicationType === "OSP") {
      pubsToSearch = ospPublications;
    } else if (publicationType === "TOI") {
      pubsToSearch = toiPublications;
    } else {
      pubsToSearch = allPublications;
    }

    if (value.trim().length > 0) {
      const filtered = pubsToSearch.filter((pub) =>
        pub.name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredPublications(filtered);
    } else {
      setFilteredPublications(pubsToSearch);
    }
  };

  const handleDowntimeChange = (
    index: number,
    field: "downtime_reason_id" | "downtime_duration",
    value: string,
  ): void => {
    setFormData((prev) => {
      const newDowntimeEntries = [...prev.downtime_entries];
      newDowntimeEntries[index] = {
        ...newDowntimeEntries[index],
        [field]: value,
      };
      return {
        ...prev,
        downtime_entries: newDowntimeEntries,
      };
    });
  };

  const handleAddDowntime = (): void => {
    setFormData((prev) => ({
      ...prev,
      downtime_entries: [
        ...prev.downtime_entries,
        {
          downtime_reason_id: "",
          downtime_duration: "00:00:00",
        },
      ],
    }));
  };

  const handleRemoveDowntime = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      downtime_entries: prev.downtime_entries.filter((_, i) => i !== index),
    }));
  };

  // Newsprint Waste Handlers
  const handleNewsprintWasteChange = (
    index: number,
    field: keyof WasteEntry,
    value: string | number,
  ): void => {
    setFormData((prev) => {
      const newEntries = [...prev.newsprint_waste_entries];
      newEntries[index] = {
        ...newEntries[index],
        [field]: value,
      };
      return {
        ...prev,
        newsprint_waste_entries: newEntries,
      };
    });
  };

  const handleAddNewsprintWaste = (): void => {
    setFormData((prev) => ({
      ...prev,
      newsprint_waste_entries: [
        ...prev.newsprint_waste_entries,
        {
          category_id: "",
          quantity: 0,
          weight_kgs: 0,
          remarks: "",
        },
      ],
    }));
  };

  const handleRemoveNewsprintWaste = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      newsprint_waste_entries: prev.newsprint_waste_entries.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  // Plates Waste Handlers
  const handlePlatesWasteChange = (
    index: number,
    field: keyof WasteEntry,
    value: string | number,
  ): void => {
    setFormData((prev) => {
      const newEntries = [...prev.plates_waste_entries];
      newEntries[index] = {
        ...newEntries[index],
        [field]: value,
      };
      return {
        ...prev,
        plates_waste_entries: newEntries,
      };
    });
  };

  const handleAddPlatesWaste = (): void => {
    setFormData((prev) => ({
      ...prev,
      plates_waste_entries: [
        ...prev.plates_waste_entries,
        {
          category_id: "",
          quantity: 0,
          remarks: "",
        },
      ],
    }));
  };

  const handleRemovePlatesWaste = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      plates_waste_entries: prev.plates_waste_entries.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const handlePublicationSelect = (pub: Publication): void => {
    setFormData((prev) => ({
      ...prev,
      publication_id: pub.id.toString(),
      custom_publication_name: "",
    }));
    setPublicationSearch(pub.name);
    setShowPublicationDropdown(false);
  };

  const handleOneTimePublication = (): void => {
    setFormData((prev) => ({
      ...prev,
      publication_id: "one-time",
      custom_publication_name: "",
    }));
    setPublicationSearch("");
    setShowPublicationDropdown(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ): void => {
    const { name, value } = e.target;

    if (name === "record_date") {
      setFormData((prev) => ({
        ...prev,
        record_date: value,
        ...(isMonthEndDate(value)
          ? {}
          : {
              ink_c: 0,
              ink_m: 0,
              ink_y: 0,
              ink_k: 0,
            }),
      }));
    } else if (name === "color_pages" || name === "bw_pages") {
      const colorPages =
        name === "color_pages"
          ? parseInt(value) || 0
          : (formData.color_pages as number);
      const bwPages =
        name === "bw_pages"
          ? parseInt(value) || 0
          : (formData.bw_pages as number);

      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value) || 0,
        total_pages: colorPages + bwPages,
      }));
    } else if (name === "plate_consumption") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value) || 0,
      }));
    } else if (["ink_c", "ink_m", "ink_y", "ink_k", "newsprint_kgs"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        [name]: parseFloat(value) || 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    try {
      if (!user) {
        setErrorMessage("User not authenticated");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (!formData.publication_id) {
        setErrorMessage("Please select or enter a publication");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (
        formData.publication_id === "one-time" &&
        !formData.custom_publication_name.trim()
      ) {
        setErrorMessage("Please enter a publication name");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (!formData.machine_id) {
        setErrorMessage("Please select a machine");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (!formData.tower_ids || formData.tower_ids.length === 0) {
        setErrorMessage("Please select at least one tower");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (formData.remarks.length > 100) {
        setErrorMessage("Remarks must be 100 characters or less");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const payload = {
        user_id: user.id,
        publication_id:
          formData.publication_id === "one-time"
            ? null
            : parseInt(formData.publication_id),
        custom_publication_name:
          formData.publication_id === "one-time"
            ? formData.custom_publication_name
            : undefined,
        po_number: parseInt(formData.po_number) || 0,
        color_pages: formData.color_pages,
        bw_pages: formData.bw_pages,
        total_pages: formData.total_pages,
        machine_id: parseInt(formData.machine_id),
        tower_ids: formData.tower_ids,
        lprs_time: formData.lprs_time,
        page_start_time: formData.page_start_time,
        page_end_time: formData.page_end_time,
        downtime_entries: formData.downtime_entries
          .filter((entry) => entry.downtime_reason_id !== "")
          .map((entry) => ({
            downtime_reason_id: parseInt(String(entry.downtime_reason_id)),
            downtime_duration: entry.downtime_duration,
          })),
        newsprint_id: formData.newsprint_id
          ? parseInt(formData.newsprint_id)
          : null,
        newsprint_kgs: parseFloat(formData.newsprint_kgs.toString()) || 0,
        plate_consumption: parseInt(formData.plate_consumption.toString()) || 0,
        newsprint_waste_entries: formData.newsprint_waste_entries
          .filter((entry) => entry.category_id !== "")
          .map((entry) => ({
            newsprint_waste_category_id: parseInt(String(entry.category_id)),
            waste_quantity: parseInt(String(entry.quantity)) || 0,
            waste_kgs: parseFloat(String(entry.weight_kgs)) || 0,
            remarks: entry.remarks,
          })),
        plates_waste_entries: formData.plates_waste_entries
          .filter((entry) => entry.category_id !== "")
          .map((entry) => ({
            plates_waste_category_id: parseInt(String(entry.category_id)),
            waste_quantity: parseInt(String(entry.quantity)) || 0,
            remarks: entry.remarks,
          })),
        remarks: formData.remarks,
        record_date: formData.record_date,
      };

      await productionAPI.createRecord(payload);
      if (isInkEntryEnabled) {
        await productionAPI.upsertMonthlyInkConsumption({
          user_id: user.id,
          production_date: formData.record_date,
          publication_type: publicationType,
          ink_c: parseFloat(formData.ink_c.toString()) || 0,
          ink_m: parseFloat(formData.ink_m.toString()) || 0,
          ink_y: parseFloat(formData.ink_y.toString()) || 0,
          ink_k: parseFloat(formData.ink_k.toString()) || 0,
        });
      }
      setSuccessMessage("Production record submitted successfully!");

      // Auto-scroll to top to show success message
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Reset form and reload records
      setFormData({
        publication_id: "",
        custom_publication_name: "",
        po_number: "",
        color_pages: 0,
        bw_pages: 0,
        total_pages: 0,
        machine_id: "",
        tower_ids: [],
        lprs_time: "00:00:00",
        page_start_time: "00:00:00",
        page_end_time: "00:00:00",
        downtime_entries: [
          {
            downtime_reason_id: "",
            downtime_duration: "00:00:00",
          },
        ],
        newsprint_id: "",
        newsprint_kgs: 0,
        plate_consumption: 0,
        ink_c: 0,
        ink_m: 0,
        ink_y: 0,
        ink_k: 0,
        newsprint_waste_entries: [
          {
            category_id: "",
            quantity: 0,
            weight_kgs: 0,
            remarks: "",
          },
        ],
        plates_waste_entries: [
          {
            category_id: "",
            quantity: 0,
            remarks: "",
          },
        ],
        remarks: "",
        record_date: new Date().toISOString().split("T")[0],
      });
      setPublicationType("");
      setPublicationSearch("");

      // Reload user records
      if (user) {
        const response = await productionAPI.getRecordsByUser(user.id);
        const records = response.data?.data || response.data || [];
        setUserRecords(Array.isArray(records) ? records : []);
      }
    } catch (error: any) {
      console.error("Submit error:", error);
      setErrorMessage(error.response?.data?.error || "Failed to submit record");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // View modal handler
  const handleViewRecord = (record: ProductionRecord): void => {
    setSelectedRecord(record);
    setModalType("view");
  };

  // Edit modal handler
  const handleEditRecord = (record: ProductionRecord): void => {
    setSelectedRecord(record);
    setEditFormData({ 
      ...record,
      record_date: formatDateForInput(record.record_date),
      tower_ids: record.tower_ids || [],
      newsprint_waste_entries: (record.newsprint_waste_entries || []).map((entry) => ({
        newsprint_waste_category_id: entry.newsprint_waste_category_id,
        waste_quantity: entry.waste_quantity,
        waste_kgs: entry.waste_kgs,
        remarks: entry.remarks || "",
      })),
      plates_waste_entries: (record.plates_waste_entries || []).map((entry) => ({
        plates_waste_category_id: entry.plates_waste_category_id,
        waste_quantity: entry.waste_quantity,
        remarks: entry.remarks || "",
      })),
    });
    setModalType("edit");
  };

  // Delete handler
  const handleDeleteRecord = async (recordId: number): Promise<void> => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await productionAPI.deleteRecord(recordId);
        setSuccessMessage("Record deleted successfully!");

        if (user) {
          const response = await productionAPI.getRecordsByUser(user.id);
          const records = response.data?.data || response.data || [];
          setUserRecords(Array.isArray(records) ? records : []);
        }
      } catch (error: any) {
        setErrorMessage(
          error.response?.data?.error || "Failed to delete record",
        );
      }
    }
  };

  // Close modal
  const closeModal = (): void => {
    setModalType(null);
    setSelectedRecord(null);
    setEditFormData(null);
  };

  // Handle edit form change - with proper null guard
  const handleEditFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ): void => {
    const { name, value } = e.target;

    if (!editFormData) return;

    setEditFormData((prev: EditFormData | null) => {
      if (!prev) return null;

      if (name === "record_date") {
        return {
          ...prev,
          record_date: value,
        };
      }

      if (name === "color_pages" || name === "bw_pages") {
        const colorPages =
          name === "color_pages" ? parseInt(value) || 0 : prev.color_pages;
        const bwPages =
          name === "bw_pages" ? parseInt(value) || 0 : prev.bw_pages;

        return {
          ...prev,
          [name]: parseInt(value) || 0,
          total_pages: colorPages + bwPages,
        };
      }

      if (
        name === "po_number" ||
        name === "machine_id" ||
        name === "newsprint_id" ||
        name === "plate_consumption" ||
        name === "newsprint_kgs"
      ) {
        return {
          ...prev,
          [name]: value,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  // Handle edit downtime change - with proper null guard
  const handleEditDowntimeChange = (
    index: number,
    field: string,
    value: string,
  ): void => {
    setEditFormData((prev: EditFormData | null) => {
      if (!prev || !prev.downtime_entries) return null;

      const newDowntimeEntries = [...prev.downtime_entries];
      if (index < 0 || index >= newDowntimeEntries.length) return prev;

      newDowntimeEntries[index] = {
        ...newDowntimeEntries[index],
        [field]: value,
      };

      return {
        ...prev,
        downtime_entries: newDowntimeEntries,
      };
    });
  };

  const handleAddEditDowntime = (): void => {
    setEditFormData((prev) =>
      prev
        ? {
            ...prev,
            downtime_entries: [
              ...(prev.downtime_entries || []),
              { downtime_reason_id: "", downtime_duration: "00:00:00" },
            ],
          }
        : null,
    );
  };

  const handleRemoveEditDowntime = (index: number): void => {
    setEditFormData((prev) =>
      prev
        ? {
            ...prev,
            downtime_entries: (prev.downtime_entries || []).filter((_, i) => i !== index),
          }
        : null,
    );
  };

  const handleEditTowerToggle = (towerId: number, checked: boolean): void => {
    setEditFormData((prev) => {
      if (!prev) return null;
      const currentTowerIds = prev.tower_ids || [];
      return {
        ...prev,
        tower_ids: checked
          ? [...currentTowerIds, towerId]
          : currentTowerIds.filter((id) => id !== towerId),
      };
    });
  };

  const handleEditNewsprintWasteChange = (
    index: number,
    field: "newsprint_waste_category_id" | "waste_quantity" | "waste_kgs" | "remarks",
    value: string | number,
  ): void => {
    setEditFormData((prev) => {
      if (!prev) return null;
      const entries = [...(prev.newsprint_waste_entries || [])];
      entries[index] = {
        ...entries[index],
        [field]: value,
      };
      return { ...prev, newsprint_waste_entries: entries };
    });
  };

  const handleAddEditNewsprintWaste = (): void => {
    setEditFormData((prev) =>
      prev
        ? {
            ...prev,
            newsprint_waste_entries: [
              ...(prev.newsprint_waste_entries || []),
              {
                newsprint_waste_category_id: "",
                waste_quantity: 0,
                waste_kgs: 0,
                remarks: "",
              },
            ],
          }
        : null,
    );
  };

  const handleRemoveEditNewsprintWaste = (index: number): void => {
    setEditFormData((prev) =>
      prev
        ? {
            ...prev,
            newsprint_waste_entries: (prev.newsprint_waste_entries || []).filter((_, i) => i !== index),
          }
        : null,
    );
  };

  const handleEditPlatesWasteChange = (
    index: number,
    field: "plates_waste_category_id" | "waste_quantity" | "remarks",
    value: string | number,
  ): void => {
    setEditFormData((prev) => {
      if (!prev) return null;
      const entries = [...(prev.plates_waste_entries || [])];
      entries[index] = {
        ...entries[index],
        [field]: value,
      };
      return { ...prev, plates_waste_entries: entries };
    });
  };

  const handleAddEditPlatesWaste = (): void => {
    setEditFormData((prev) =>
      prev
        ? {
            ...prev,
            plates_waste_entries: [
              ...(prev.plates_waste_entries || []),
              {
                plates_waste_category_id: "",
                waste_quantity: 0,
                remarks: "",
              },
            ],
          }
        : null,
    );
  };

  const handleRemoveEditPlatesWaste = (index: number): void => {
    setEditFormData((prev) =>
      prev
        ? {
            ...prev,
            plates_waste_entries: (prev.plates_waste_entries || []).filter((_, i) => i !== index),
          }
        : null,
    );
  };

  // Handle edit submit - with proper null guard and type conversion
  const handleEditSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (!editFormData) {
      setErrorMessage("No record data to update");
      return;
    }

    try {
      // Convert form data to proper API payload type
      const payload: ProductionRecordPayload = {
        id: editFormData.id,
        user_id: editFormData.user_id,
        publication_id: editFormData.publication_id,
        custom_publication_name:
          editFormData.custom_publication_name === null
            ? undefined
            : editFormData.custom_publication_name,
        po_number: parseInt(String(editFormData.po_number)) || 0,
        color_pages: editFormData.color_pages,
        bw_pages: editFormData.bw_pages,
        total_pages: editFormData.total_pages,
        machine_id: parseInt(String(editFormData.machine_id)) || 0,
        lprs_time: editFormData.lprs_time,
        page_start_time: editFormData.page_start_time,
        page_end_time: editFormData.page_end_time,
        downtime_entries: (editFormData.downtime_entries || [])
          .filter(
            (entry: any) =>
              entry.downtime_reason_id !== "" && entry.downtime_reason_id !== 0,
          )
          .map((entry: any) => ({
            downtime_reason_id: parseInt(String(entry.downtime_reason_id)) || 0,
            downtime_duration: entry.downtime_duration,
          })),
        tower_ids: editFormData.tower_ids || [],
        newsprint_waste_entries: (editFormData.newsprint_waste_entries || [])
          .filter((entry) => entry.newsprint_waste_category_id !== "")
          .map((entry) => ({
            newsprint_waste_category_id:
              parseInt(String(entry.newsprint_waste_category_id)) || 0,
            waste_quantity: parseInt(String(entry.waste_quantity)) || 0,
            waste_kgs: parseFloat(String(entry.waste_kgs)) || 0,
            remarks: entry.remarks || "",
          })),
        plates_waste_entries: (editFormData.plates_waste_entries || [])
          .filter((entry) => entry.plates_waste_category_id !== "")
          .map((entry) => ({
            plates_waste_category_id:
              parseInt(String(entry.plates_waste_category_id)) || 0,
            waste_quantity: parseInt(String(entry.waste_quantity)) || 0,
            remarks: entry.remarks || "",
          })),
        newsprint_id: editFormData.newsprint_id
          ? parseInt(String(editFormData.newsprint_id))
          : null,
        newsprint_kgs: parseFloat(String(editFormData.newsprint_kgs)) || 0,
        plate_consumption:
          parseInt(String(editFormData.plate_consumption)) || 0,
        remarks: editFormData.remarks || "",
        record_date: editFormData.record_date,
      };

      await productionAPI.updateRecord(editFormData.id, payload);
      setSuccessMessage("Record updated successfully!");
      closeModal();

      // Reload user records
      if (user) {
        const response = await productionAPI.getRecordsByUser(user.id);
        const records = response.data?.data || response.data || [];
        setUserRecords(Array.isArray(records) ? records : []);
      }
    } catch (error: any) {
      console.error("Error updating record:", error);
      setErrorMessage(error.response?.data?.error || "Failed to update record");
    }
  };
  const getPublicationName = (
    pubId: number | null,
    customName: string | null,
  ): string => {
    if (customName) return customName;
    if (pubId) {
      const pub = allPublications.find((p) => p.id === pubId);
      return pub?.name || "Unknown";
    }
    return "N/A";
  };

  const getMachineName = (machineId: number): string => {
    const machine = machines.find((m) => m.id === machineId);
    return machine?.name || "Unknown";
  };

  const getNewsprintName = (newsprintId: number | null): string => {
    if (!newsprintId) return "N/A";
    const newsprint = newsprintTypes.find((n) => n.id === newsprintId);
    return newsprint?.name || "Unknown";
  };

  const getTowerNames = (towerIds: number[] = []): string => {
    if (!towerIds.length) return "N/A";
    return towerIds
      .map((towerId) => towers.find((tower) => tower.id === towerId)?.name || `Tower ${towerId}`)
      .join(", ");
  };

  const getNewsprintWasteCategoryName = (categoryId: number | string): string => {
    const parsedId = parseInt(String(categoryId), 10);
    const category = newsprintWasteCategories.find((cat) => cat.id === parsedId);
    return category?.name || "Unknown";
  };

  const getPlatesWasteCategoryName = (categoryId: number | string): string => {
    const parsedId = parseInt(String(categoryId), 10);
    const category = platesWasteCategories.find((cat) => cat.id === parsedId);
    return category?.name || "Unknown";
  };

  const getNewsprintWasteTotal = (record: ProductionRecord): number =>
    (record.newsprint_waste_entries || []).reduce(
      (sum, entry) => sum + (parseInt(String(entry.waste_quantity), 10) || 0),
      0,
    );

  const getPlatesWasteTotal = (record: ProductionRecord): number =>
    (record.plates_waste_entries || []).reduce(
      (sum, entry) => sum + (parseInt(String(entry.waste_quantity), 10) || 0),
      0,
    );

  const getLprsScheduleForPublicationId = (
    publicationId: number | string | null,
  ): LprsSchedule | undefined => {
    if (!publicationId || publicationId === "one-time") return undefined;
    const parsedId =
      typeof publicationId === "number" ? publicationId : parseInt(publicationId, 10);
    if (Number.isNaN(parsedId)) return undefined;
    return activeLprsSchedules.find((schedule) => schedule.publication_id === parsedId);
  };

  const getLprsComparison = (
    publicationId: number | string | null,
    actualLprsTime: string,
  ): { status: "Early" | "On time" | "Delayed"; difference: string } | null => {
    const schedule = getLprsScheduleForPublicationId(publicationId);
    if (!schedule) return null;

    const actualSeconds = timeToSeconds(actualLprsTime);
    const scheduledSeconds = timeToSeconds(schedule.scheduled_lprs_time);
    if (actualSeconds === null || scheduledSeconds === null) return null;

    const differenceSeconds = getSignedClockDifferenceSeconds(actualSeconds, scheduledSeconds);
    return {
      status:
        differenceSeconds > 0
          ? "Delayed"
          : differenceSeconds < 0
            ? "Early"
            : "On time",
      difference: formatTimeDifference(differenceSeconds),
    };
  };

  const selectedLprsSchedule = getLprsScheduleForPublicationId(formData.publication_id);
  const selectedLprsComparison = getLprsComparison(formData.publication_id, formData.lprs_time);
  const hasAnyRecordLprsSchedule = userRecords.some((record) =>
    Boolean(getLprsScheduleForPublicationId(record.publication_id)),
  );

  if (isLoading) {
    return (
      <div>
        <UserNavbar />
        <div className="loading">
          ⏳ Loading publications for your location...
        </div>
      </div>
    );
  }

  return (
    <div>
      <UserNavbar />
      <div className="user-dashboard">
        <div className="dashboard-header">
          <h2>Welcome, {user?.name}</h2>
          <p>📍 Location: {user?.location}</p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === "form" ? "active" : ""}`}
            onClick={() => setActiveTab("form")}
          >
            Production Form
          </button>
          <button
            className={`tab-button ${activeTab === "reports" ? "active" : ""}`}
            onClick={() => setActiveTab("reports")}
          >
            Reports ({userRecords.length})
          </button>
        </div>

        {/* Form Tab */}
        {activeTab === "form" && (
          <div className="form-container">
            <h3>Daily Production Record</h3>

            {successMessage && (
              <div className="success-message">{successMessage}</div>
            )}
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}

            <form onSubmit={handleSubmit} className="production-form">
              <div className="form-section">
                <h4>Production Date</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="record_date">Production Date *</label>
                    <input
                      id="record_date"
                      type="date"
                      name="record_date"
                      value={formData.record_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* ✅ Publication Type Selector */}
              <div className="form-section">
                <h4>Select Publication Type 📰</h4>
                <div className="publication-type-selector">
                  <button
                    type="button"
                    className={`type-btn ${publicationType === "VK" ? "active" : ""}`}
                    onClick={() => handlePublicationTypeChange("VK")}
                    disabled={vkPublications.length === 0}
                    title={
                      vkPublications.length === 0
                        ? "No VK publications for your location"
                        : ""
                    }
                  >
                    📰 VK ({vkPublications.length})
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${publicationType === "OSP" ? "active" : ""}`}
                    onClick={() => handlePublicationTypeChange("OSP")}
                    disabled={ospPublications.length === 0}
                    title={
                      ospPublications.length === 0
                        ? "No OSP publications for your location"
                        : ""
                    }
                  >
                    📰 OSP ({ospPublications.length})
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${publicationType === "TOI" ? "active" : ""}`}
                    onClick={() => handlePublicationTypeChange("TOI")}
                    disabled={toiPublications.length === 0}
                    title={
                      toiPublications.length === 0
                        ? "No TOI publications for your location"
                        : ""
                    }
                  >
                    📰 TOI ({toiPublications.length})
                  </button>
                </div>
              </div>

              {/* Publication & copies printed details */}
              <div className="form-section">
                <h4>Publication & Copies Printed Details</h4>

                {publicationType && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="publication">
                        {publicationType} Publication *
                      </label>
                      <div
                        className="publication-search-wrapper"
                        ref={publicationDropdownRef}
                      >
                        <input
                          type="text"
                          id="publication"
                          placeholder={`Search ${publicationType} publications...`}
                          value={publicationSearch}
                          onChange={(e) =>
                            handlePublicationSearch(e.target.value)
                          }
                          onFocus={() => {
                            setShowPublicationDropdown(true);
                            if (publicationSearch.trim().length === 0) {
                              if (publicationType === "VK") {
                                setFilteredPublications(vkPublications);
                              } else if (publicationType === "OSP") {
                                setFilteredPublications(ospPublications);
                              } else if (publicationType === "TOI") {
                                setFilteredPublications(toiPublications);
                              }
                            }
                          }}
                          className="publication-search-input"
                        />
                        {showPublicationDropdown && (
                          <div className="publication-dropdown">
                            {filteredPublications.length > 0 ? (
                              <>
                                {filteredPublications.map((pub) => (
                                  <div
                                    key={pub.id}
                                    className="publication-option"
                                    onClick={() => handlePublicationSelect(pub)}
                                  >
                                    <span className="pub-name">{pub.name}</span>
                                    <span className="pub-code">{pub.code}</span>
                                  </div>
                                ))}
                                <div className="publication-divider"></div>
                                <div
                                  className="publication-option other-option"
                                  onClick={handleOneTimePublication}
                                >
                                  <span>
                                    + One Time (Enter Publication Name)
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="publication-option disabled">
                                  No publications found for {user?.location}
                                </div>
                                <div className="publication-divider"></div>
                                <div
                                  className="publication-option other-option"
                                  onClick={handleOneTimePublication}
                                >
                                  <span>
                                    + One Time (Enter Publication Name)
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {formData.publication_id === "one-time" && (
                        <div
                          className="form-group"
                          style={{ marginTop: "10px" }}
                        >
                          <label htmlFor="custom_publication">
                            Enter Publication Name *
                          </label>
                          <input
                            id="custom_publication"
                            type="text"
                            name="custom_publication_name"
                            value={formData.custom_publication_name}
                            onChange={handleInputChange}
                            placeholder="Enter publication name"
                            required
                          />
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="po_number">No. of Copies Printed *</label>
                      <input
                        id="po_number"
                        type="number"
                        name="po_number"
                        value={formData.po_number}
                        onChange={handleInputChange}
                        placeholder="Enter no. of copies printed"
                        required
                      />
                    </div>
                  </div>
                )}

                {!publicationType && (
                  <div className="form-message">
                    <p>👆 Please select a publication type above to continue</p>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h4>Pages</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="color_pages">Color Pages</label>
                    <input
                      id="color_pages"
                      type="number"
                      name="color_pages"
                      value={formData.color_pages}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bw_pages">Black & White Pages</label>
                    <input
                      id="bw_pages"
                      type="number"
                      name="bw_pages"
                      value={formData.bw_pages}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="total_pages">Total Pages</label>
                    <input
                      id="total_pages"
                      type="number"
                      name="total_pages"
                      value={formData.total_pages}
                      readOnly
                      className="total-pages-display"
                    />
                    <small style={{ color: "#666" }}>Auto-calculated</small>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Machine & Timing</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="machine">Machine *</label>
                    <select
                      id="machine"
                      name="machine_id"
                      value={formData.machine_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Machine</option>
                      {machines.map((mach) => (
                        <option key={mach.id} value={mach.id}>
                          {mach.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="towers">Towers *</label>
                    <div className="towers-selection">
                      {towers.map((tower) => (
                        <label key={tower.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.tower_ids.includes(tower.id)}
                            onChange={(e) => {
                              const newTowerIds = e.target.checked
                                ? [...formData.tower_ids, tower.id]
                                : formData.tower_ids.filter((id) => id !== tower.id);
                              setFormData({
                                ...formData,
                                tower_ids: newTowerIds,
                              });
                            }}
                          />
                          {tower.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="lprs_time">LPRS Time (HH:MM:SS)</label>
                    <input
                      id="lprs_time"
                      type="time"
                      name="lprs_time"
                      value={formData.lprs_time}
                      onChange={handleInputChange}
                      step="1"
                    />
                    {selectedLprsSchedule && (
                      <div className="lprs-schedule-hint">
                        <span>Fixed LPRS: {formatClockTime(selectedLprsSchedule.scheduled_lprs_time)}</span>
                        {selectedLprsComparison && (
                          <strong className={`lprs-status ${selectedLprsComparison.status.toLowerCase().replace(" ", "-")}`}>
                            {selectedLprsComparison.status}
                            {selectedLprsComparison.difference !== "00:00:00"
                              ? ` (${selectedLprsComparison.difference})`
                              : ""}
                          </strong>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="page_start_time">
                      Print Start Time (HH:MM:SS)
                    </label>
                    <input
                      id="page_start_time"
                      type="time"
                      name="page_start_time"
                      value={formData.page_start_time}
                      onChange={handleInputChange}
                      step="1"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="page_end_time">
                      Print End Time (HH:MM:SS)
                    </label>
                    <input
                      id="page_end_time"
                      type="time"
                      name="page_end_time"
                      value={formData.page_end_time}
                      onChange={handleInputChange}
                      step="1"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <h4>Downtime Details</h4>
                  <button
                    type="button"
                    className="btn-add-downtime"
                    onClick={handleAddDowntime}
                    title="Add another downtime entry"
                  >
                    + Add Downtime
                  </button>
                </div>

                {formData.downtime_entries.map((entry, index) => (
                  <div key={index} className="downtime-entry">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`downtime_reason_${index}`}>
                          Downtime Reason
                        </label>
                        <select
                          id={`downtime_reason_${index}`}
                          value={entry.downtime_reason_id}
                          onChange={(e) =>
                            handleDowntimeChange(
                              index,
                              "downtime_reason_id",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Reason</option>
                          {downtimeReasons.map((reason) => (
                            <option key={reason.id} value={reason.id}>
                              {reason.reason || reason.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor={`downtime_duration_${index}`}>
                          Duration (HH:MM:SS)
                        </label>
                        <input
                          id={`downtime_duration_${index}`}
                          type="text"
                          placeholder="00:00:00"
                          value={entry.downtime_duration}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[0-9:]*$/.test(val) && val.length <= 8) {
                              handleDowntimeChange(
                                index,
                                "downtime_duration",
                                val,
                              );
                            }
                          }}
                          onBlur={(e) => {
                            const val = e.target.value;
                            const formatted = formatDuration(val);
                            handleDowntimeChange(
                              index,
                              "downtime_duration",
                              formatted,
                            );
                          }}
                          maxLength={8}
                        />
                        <small style={{ color: "#666" }}>
                          Enter as HH:MM:SS
                        </small>
                      </div>

                      {formData.downtime_entries.length > 1 && (
                        <div className="form-group btn-group">
                          <button
                            type="button"
                            className="btn-remove-downtime"
                            onClick={() => handleRemoveDowntime(index)}
                            title="Remove this downtime entry"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-section">
                <h4>Newsprint & Consumption</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="newsprint">Newsprint Type</label>
                    <select
                      id="newsprint"
                      name="newsprint_id"
                      value={formData.newsprint_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Newsprint</option>
                      {newsprintTypes.map((np) => (
                        <option key={np.id} value={np.id}>
                          {np.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="newsprint_kgs">Enter KGs (Decimal)</label>
                    <input
                      id="newsprint_kgs"
                      type="number"
                      name="newsprint_kgs"
                      value={formData.newsprint_kgs}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="Enter KGs (e.g., 10.50)"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="plate_consumption">
                      Total Plate Consumption
                    </label>
                    <input
                      id="plate_consumption"
                      type="number"
                      name="plate_consumption"
                      value={formData.plate_consumption}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Newsprint Waste Categories */}
              <div className="form-section">
                <div className="section-header">
                  <h4>Newsprint Waste Categories</h4>
                  <button
                    type="button"
                    className="btn-add-downtime"
                    onClick={handleAddNewsprintWaste}
                    title="Add another newsprint waste entry"
                  >
                    + Add Newsprint Waste
                  </button>
                </div>

                {formData.newsprint_waste_entries.map((entry, index) => (
                  <div key={index} className="downtime-entry">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`newsprint_waste_category_${index}`}>
                          Waste Category
                        </label>
                        <select
                          id={`newsprint_waste_category_${index}`}
                          value={entry.category_id}
                          onChange={(e) =>
                            handleNewsprintWasteChange(
                              index,
                              "category_id",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Category</option>
                          {newsprintWasteCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor={`newsprint_waste_qty_${index}`}>
                          Quantity (Pages/Copies)
                        </label>
                        <input
                          id={`newsprint_waste_qty_${index}`}
                          type="number"
                          value={entry.quantity}
                          onChange={(e) =>
                            handleNewsprintWasteChange(
                              index,
                              "quantity",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          min="0"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`newsprint_waste_kgs_${index}`}>
                          Weight (KGs)
                        </label>
                        <input
                          id={`newsprint_waste_kgs_${index}`}
                          type="number"
                          value={entry.weight_kgs}
                          onChange={(e) =>
                            handleNewsprintWasteChange(
                              index,
                              "weight_kgs",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          min="0"
                          step="0.01"
                        />
                      </div>

                      {formData.newsprint_waste_entries.length > 1 && (
                        <div className="form-group btn-group">
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemoveNewsprintWaste(index)}
                            title="Remove this waste entry"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`newsprint_waste_remarks_${index}`}>
                          Remarks
                        </label>
                        <input
                          id={`newsprint_waste_remarks_${index}`}
                          type="text"
                          placeholder="Optional remarks"
                          value={entry.remarks}
                          onChange={(e) =>
                            handleNewsprintWasteChange(
                              index,
                              "remarks",
                              e.target.value,
                            )
                          }
                          maxLength={100}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Plates Waste Categories */}
              <div className="form-section">
                <div className="section-header">
                  <h4>Plates Waste Categories</h4>
                  <button
                    type="button"
                    className="btn-add-downtime"
                    onClick={handleAddPlatesWaste}
                    title="Add another plates waste entry"
                  >
                    + Add Plates Waste
                  </button>
                </div>

                {formData.plates_waste_entries.map((entry, index) => (
                  <div key={index} className="downtime-entry">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`plates_waste_category_${index}`}>
                          Waste Category
                        </label>
                        <select
                          id={`plates_waste_category_${index}`}
                          value={entry.category_id}
                          onChange={(e) =>
                            handlePlatesWasteChange(
                              index,
                              "category_id",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">Select Category</option>
                          {platesWasteCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor={`plates_waste_qty_${index}`}>
                          Quantity (Number of Plates)
                        </label>
                        <input
                          id={`plates_waste_qty_${index}`}
                          type="number"
                          value={entry.quantity}
                          onChange={(e) =>
                            handlePlatesWasteChange(
                              index,
                              "quantity",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          min="0"
                        />
                      </div>

                      {formData.plates_waste_entries.length > 1 && (
                        <div className="form-group btn-group">
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemovePlatesWaste(index)}
                            title="Remove this waste entry"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`plates_waste_remarks_${index}`}>
                          Remarks
                        </label>
                        <input
                          id={`plates_waste_remarks_${index}`}
                          type="text"
                          placeholder="Optional remarks"
                          value={entry.remarks}
                          onChange={(e) =>
                            handlePlatesWasteChange(
                              index,
                              "remarks",
                              e.target.value,
                            )
                          }
                          maxLength={100}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {supportsMonthlyInk && (
              <div className="form-section">
                <h4>Monthly Overall {publicationType} Ink Consumption (KGs)</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ink_c">C</label>
                    <input
                      id="ink_c"
                      type="number"
                      name="ink_c"
                      value={formData.ink_c}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="C ink in KGs"
                      disabled={!isInkEntryEnabled}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ink_m">M</label>
                    <input
                      id="ink_m"
                      type="number"
                      name="ink_m"
                      value={formData.ink_m}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="M ink in KGs"
                      disabled={!isInkEntryEnabled}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ink_y">Y</label>
                    <input
                      id="ink_y"
                      type="number"
                      name="ink_y"
                      value={formData.ink_y}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="Y ink in KGs"
                      disabled={!isInkEntryEnabled}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ink_k">K</label>
                    <input
                      id="ink_k"
                      type="number"
                      name="ink_k"
                      value={formData.ink_k}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="K ink in KGs"
                      disabled={!isInkEntryEnabled}
                    />
                  </div>
                </div>
                <small style={{ color: "#666" }}>
                  Enter once for all {publicationType} publications for this location and month. It is not edition-wise or publication-wise, and repeated month-end submissions update the same monthly {publicationType} entry instead of creating duplicates.
                </small>
              </div>
              )}

              <div className="form-section">
                <h4>Additional Info</h4>
                <div className="form-group">
                  <label htmlFor="remarks">Remarks (Max 100 characters)</label>
                  <textarea
                    id="remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    placeholder="Enter any remarks"
                    maxLength={100}
                    rows={3}
                  />
                  <small>{formData.remarks.length}/100</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  Submit Record
                </button>
                <button type="reset" className="btn-reset">
                  Clear
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div className="reports-container">
            <h3>My Production Records</h3>

            {successMessage && (
              <div className="success-message">{successMessage}</div>
            )}
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}

            {userRecords.length > 0 ? (
              <div className="table-wrapper">
                <table className="records-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
	                      <th>Publication</th>
	                      <th>No. of Copies Printed</th>
	                      <th>Machine</th>
	                      <th>Total Pg</th>
	                      <th>LPRS Time</th>
	                      {hasAnyRecordLprsSchedule && <th>Fixed LPRS</th>}
	                      {hasAnyRecordLprsSchedule && <th>LPRS Status</th>}
	                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userRecords.map((record) => {
                      const schedule = getLprsScheduleForPublicationId(record.publication_id);
                      const comparison = getLprsComparison(record.publication_id, record.lprs_time);

                      return (
                        <tr key={record.id}>
                          <td>
                            {new Date(record.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            {getPublicationName(
                              record.publication_id,
                              record.custom_publication_name,
                            )}
                          </td>
	                          <td>{record.po_number}</td>
	                          <td>{getMachineName(record.machine_id)}</td>
	                          <td>
	                            <strong>{record.total_pages}</strong>
	                          </td>
                          <td>{record.lprs_time}</td>
                          {hasAnyRecordLprsSchedule && (
                            <td>{schedule ? formatClockTime(schedule.scheduled_lprs_time) : ""}</td>
                          )}
                          {hasAnyRecordLprsSchedule && (
                            <td>
                              {comparison && (
                                <span className={`lprs-status ${comparison.status.toLowerCase().replace(" ", "-")}`}>
                                  {comparison.status}
                                  {comparison.difference !== "00:00:00"
                                    ? ` (${comparison.difference})`
                                    : ""}
                                </span>
                              )}
                            </td>
                          )}
	                          <td className="actions-cell">
                            <button
                              className="btn-view"
                              onClick={() => handleViewRecord(record)}
                              title="View details"
                            >
                              View
                            </button>
                          <button
                            className="btn-edit"
                            onClick={() => handleEditRecord(record)}
                            title="Edit record"
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteRecord(record.id)}
                            title="Delete record"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-records">
                <p>No production records submitted yet.</p>
                <button
                  className="btn-go-to-form"
                  onClick={() => setActiveTab("form")}
                >
                  Go to Form →
                </button>
              </div>
            )}
          </div>
        )}

        {/* View Modal */}
        {modalType === "view" && selectedRecord && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Record Details</h3>
                <button className="modal-close" onClick={closeModal}>
                  ✕
                </button>
              </div>

              <div className="modal-body">
                {/* Publication & PO Section */}
                <div className="detail-row">
                  <span className="label">Publication:</span>
                  <span className="value">
                    {getPublicationName(
                      selectedRecord.publication_id,
                      selectedRecord.custom_publication_name,
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">No. of Copies Printed:</span>
                  <span className="value">{selectedRecord.po_number}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Production Date:</span>
                  <span className="value">
                    {formatDateForDisplay(selectedRecord.record_date)}
                  </span>
                </div>

                {/* Pages Section */}
                <div className="detail-row">
                  <span className="label">Pages:</span>
                  <span className="value">
                    Color: {selectedRecord.color_pages} | BW:{" "}
                    {selectedRecord.bw_pages} | Total:{" "}
                    {selectedRecord.total_pages}
                  </span>
                </div>

                {/* Machine & Timing Section */}
                <div className="detail-section">
                  <h4>Machine & Timing Background</h4>
                  <div className="detail-row">
                    <span className="label">Machine:</span>
                    <span className="value">
                      {getMachineName(selectedRecord.machine_id)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Towers:</span>
                    <span className="value">
                      {getTowerNames(selectedRecord.tower_ids || [])}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">LPRS Time:</span>
                    <span className="value">{selectedRecord.lprs_time}</span>
                  </div>
                  {getLprsScheduleForPublicationId(selectedRecord.publication_id) && (
                    <div className="detail-row">
                      <span className="label">LPRS Status:</span>
                      <span className="value">
                        {(() => {
                          const comparison = getLprsComparison(
                            selectedRecord.publication_id,
                            selectedRecord.lprs_time,
                          );
                          return comparison
                            ? `${comparison.status}${comparison.difference !== "00:00:00" ? ` (${comparison.difference})` : ""}`
                            : "N/A";
                        })()}
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Print Start Time:</span>
                    <span className="value">
                      {selectedRecord.page_start_time}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Print End Time:</span>
                    <span className="value">
                      {selectedRecord.page_end_time}
                    </span>
                  </div>
                </div>

                {/* Newsprint & Consumption Section */}
                <div className="detail-section">
                  <h4>Newsprint & Consumption</h4>
                  <div className="detail-row">
                    <span className="label">Newsprint Type:</span>
                    <span className="value">
                      {getNewsprintName(selectedRecord.newsprint_id)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">KGs Consumed:</span>
                    <span className="value">
                      {parseFloat(
                        selectedRecord.newsprint_kgs?.toString() || "0",
                      ).toFixed(2)}{" "}
                      kg
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Plate Consumption:</span>
                    <span className="value">
                      {selectedRecord.plate_consumption}
                    </span>
                  </div>
                </div>

                {/* Waste Details */}
                <div className="detail-section">
                  <h4>Waste Details</h4>
                  <div className="detail-row">
                    <span className="label">Pages Wasted:</span>
                    <span className="value">{getNewsprintWasteTotal(selectedRecord)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Plates Wasted:</span>
                    <span className="value">{getPlatesWasteTotal(selectedRecord)}</span>
                  </div>

                  {(selectedRecord.newsprint_waste_entries || []).length > 0 && (
                    <div className="waste-detail-list">
                      <strong>Newsprint Waste</strong>
                      {(selectedRecord.newsprint_waste_entries || []).map((entry, index) => (
                        <div key={`view-newsprint-waste-${index}`} className="waste-detail-item">
                          <span>{getNewsprintWasteCategoryName(entry.newsprint_waste_category_id)}</span>
                          <span>{entry.waste_quantity || 0} qty</span>
                          <span>{Number(entry.waste_kgs || 0).toFixed(2)} kg</span>
                          {entry.remarks && <span>{entry.remarks}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {(selectedRecord.plates_waste_entries || []).length > 0 && (
                    <div className="waste-detail-list">
                      <strong>Plates Waste</strong>
                      {(selectedRecord.plates_waste_entries || []).map((entry, index) => (
                        <div key={`view-plates-waste-${index}`} className="waste-detail-item">
                          <span>{getPlatesWasteCategoryName(entry.plates_waste_category_id)}</span>
                          <span>{entry.waste_quantity || 0} qty</span>
                          {entry.remarks && <span>{entry.remarks}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Downtime Details */}
                {selectedRecord.downtime_entries &&
                  selectedRecord.downtime_entries.length > 0 && (
                    <div className="detail-section">
                      <h4>Downtime Details</h4>
                      <div className="downtime-list">
                        {selectedRecord.downtime_entries.map((dt, idx) => {
                          const reason = downtimeReasons.find(
                            (r) => r.id === dt.downtime_reason_id,
                          );
                          return (
                            <div key={idx} className="downtime-item-detailed">
                              <span className="reason">
                                {reason?.reason || reason?.name || "Unknown"}
                              </span>
                              <span className="duration">
                                {dt.downtime_duration}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* Remarks */}
                {selectedRecord.remarks && (
                  <div className="detail-section">
                    <h4>Remarks</h4>
                    <p className="remarks-text">{selectedRecord.remarks}</p>
                  </div>
                )}

                {/* Record Metadata */}
                <div className="detail-section">
                  <h4>Record Information</h4>

                  <div className="detail-row">
                    <span className="label">Submitted On:</span>
                    <span className="value">
                      {new Date(selectedRecord.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-close" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {modalType === "edit" && editFormData && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-content modal-edit"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Edit Record</h3>
                <button className="modal-close" onClick={closeModal}>
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleEditSubmit} className="edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Publication</label>
                      <input
                        type="text"
                        value={getPublicationName(
                          editFormData.publication_id,
                          editFormData.custom_publication_name as string | null,
                        )}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label>No. of Copies Printed</label>
                      <input
                        type="number"
                        name="po_number"
                        value={editFormData.po_number || ""}
                        onChange={handleEditFormChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Color Pages</label>
                      <input
                        type="number"
                        name="color_pages"
                        value={editFormData.color_pages || 0}
                        onChange={handleEditFormChange}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>BW Pages</label>
                      <input
                        type="number"
                        name="bw_pages"
                        value={editFormData.bw_pages || 0}
                        onChange={handleEditFormChange}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Total Pages</label>
                      <input
                        type="number"
                        value={editFormData.total_pages || 0}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Machine</label>
                      <select
                        name="machine_id"
                        value={editFormData.machine_id || ""}
                        onChange={handleEditFormChange}
                        required
                      >
                        <option value="">Select Machine</option>
                        {machines.map((machine) => (
                          <option key={machine.id} value={machine.id}>
                            {machine.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>LPRS Time</label>
                      <input
                        type="time"
                        name="lprs_time"
                        value={editFormData.lprs_time || "00:00:00"}
                        onChange={handleEditFormChange}
                        step="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Print Start Time</label>
                      <input
                        type="time"
                        name="page_start_time"
                        value={editFormData.page_start_time || "00:00:00"}
                        onChange={handleEditFormChange}
                        step="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Print End Time</label>
                      <input
                        type="time"
                        name="page_end_time"
                        value={editFormData.page_end_time || "00:00:00"}
                        onChange={handleEditFormChange}
                        step="1"
                      />
                    </div>
                  </div>

                  <div className="form-section-edit">
                    <h4>Towers</h4>
                    <div className="edit-checkbox-grid">
                      {towers.map((tower) => (
                        <label key={`edit-tower-${tower.id}`} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={(editFormData.tower_ids || []).includes(tower.id)}
                            onChange={(e) => handleEditTowerToggle(tower.id, e.target.checked)}
                          />
                          {tower.name}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Newsprint Type</label>
                      <select
                        name="newsprint_id"
                        value={editFormData.newsprint_id || ""}
                        onChange={handleEditFormChange}
                      >
                        <option value="">Select Newsprint</option>
                        {newsprintTypes.map((np) => (
                          <option key={np.id} value={np.id}>
                            {np.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>KGs</label>
                      <input
                        type="number"
                        name="newsprint_kgs"
                        value={editFormData.newsprint_kgs || 0}
                        onChange={handleEditFormChange}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label>Plate Consumption</label>
                      <input
                        type="number"
                        name="plate_consumption"
                        value={editFormData.plate_consumption || 0}
                        onChange={handleEditFormChange}
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="form-section-edit">
                    <div className="section-header">
                      <h4>Downtime Entries</h4>
                      <button
                        type="button"
                        className="btn-add-downtime"
                        onClick={handleAddEditDowntime}
                      >
                        + Add Downtime
                      </button>
                    </div>
                    {editFormData.downtime_entries &&
                    Array.isArray(editFormData.downtime_entries) &&
                    editFormData.downtime_entries.length > 0 ? (
                      editFormData.downtime_entries.map(
                        (entry: any, index: number) => (
                          <div key={index} className="downtime-edit-row">
                            <select
                              value={entry?.downtime_reason_id || ""}
                              onChange={(e) =>
                                handleEditDowntimeChange(
                                  index,
                                  "downtime_reason_id",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select Reason</option>
                              {downtimeReasons.map((reason) => (
                                <option key={reason.id} value={reason.id}>
                                  {reason.reason || reason.name || "Unknown"}
                                </option>
                              ))}
                            </select>
                            <input
                              type="time"
                              placeholder="HH:MM:SS"
                              value={entry?.downtime_duration || "00:00:00"}
                              onChange={(e) =>
                                handleEditDowntimeChange(
                                  index,
                                  "downtime_duration",
                                  e.target.value,
                                )
                              }
                              step="1"
                            />
                            <button
                              type="button"
                              className="btn-remove"
                              onClick={() => handleRemoveEditDowntime(index)}
                            >
                              Remove
                            </button>
                          </div>
                        ),
                      )
                    ) : (
                      <p className="no-downtime">No downtime entries</p>
                    )}
                  </div>

                  <div className="form-section-edit">
                    <div className="section-header">
                      <h4>Newsprint Waste Categories</h4>
                      <button
                        type="button"
                        className="btn-add-downtime"
                        onClick={handleAddEditNewsprintWaste}
                      >
                        + Add Newsprint Waste
                      </button>
                    </div>
                    {(editFormData.newsprint_waste_entries || []).length > 0 ? (
                      (editFormData.newsprint_waste_entries || []).map((entry, index) => (
                        <div key={`edit-newsprint-waste-${index}`} className="downtime-entry">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Waste Category</label>
                              <select
                                value={entry.newsprint_waste_category_id || ""}
                                onChange={(e) =>
                                  handleEditNewsprintWasteChange(index, "newsprint_waste_category_id", e.target.value)
                                }
                              >
                                <option value="">Select Category</option>
                                {newsprintWasteCategories.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Quantity</label>
                              <input
                                type="number"
                                value={entry.waste_quantity || 0}
                                onChange={(e) =>
                                  handleEditNewsprintWasteChange(index, "waste_quantity", parseInt(e.target.value, 10) || 0)
                                }
                                min="0"
                              />
                            </div>
                            <div className="form-group">
                              <label>Weight (KGs)</label>
                              <input
                                type="number"
                                value={entry.waste_kgs || 0}
                                onChange={(e) =>
                                  handleEditNewsprintWasteChange(index, "waste_kgs", parseFloat(e.target.value) || 0)
                                }
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <div className="form-group btn-group">
                              <button
                                type="button"
                                className="btn-remove"
                                onClick={() => handleRemoveEditNewsprintWaste(index)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Remarks</label>
                            <input
                              type="text"
                              value={entry.remarks || ""}
                              onChange={(e) =>
                                handleEditNewsprintWasteChange(index, "remarks", e.target.value)
                              }
                              maxLength={100}
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-downtime">No newsprint waste entries</p>
                    )}
                  </div>

                  <div className="form-section-edit">
                    <div className="section-header">
                      <h4>Plates Waste Categories</h4>
                      <button
                        type="button"
                        className="btn-add-downtime"
                        onClick={handleAddEditPlatesWaste}
                      >
                        + Add Plates Waste
                      </button>
                    </div>
                    {(editFormData.plates_waste_entries || []).length > 0 ? (
                      (editFormData.plates_waste_entries || []).map((entry, index) => (
                        <div key={`edit-plates-waste-${index}`} className="downtime-entry">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Waste Category</label>
                              <select
                                value={entry.plates_waste_category_id || ""}
                                onChange={(e) =>
                                  handleEditPlatesWasteChange(index, "plates_waste_category_id", e.target.value)
                                }
                              >
                                <option value="">Select Category</option>
                                {platesWasteCategories.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Quantity</label>
                              <input
                                type="number"
                                value={entry.waste_quantity || 0}
                                onChange={(e) =>
                                  handleEditPlatesWasteChange(index, "waste_quantity", parseInt(e.target.value, 10) || 0)
                                }
                                min="0"
                              />
                            </div>
                            <div className="form-group btn-group">
                              <button
                                type="button"
                                className="btn-remove"
                                onClick={() => handleRemoveEditPlatesWaste(index)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Remarks</label>
                            <input
                              type="text"
                              value={entry.remarks || ""}
                              onChange={(e) =>
                                handleEditPlatesWasteChange(index, "remarks", e.target.value)
                              }
                              maxLength={100}
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-downtime">No plates waste entries</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Remarks</label>
                    <textarea
                      name="remarks"
                      value={editFormData.remarks || ""}
                      onChange={handleEditFormChange}
                      maxLength={100}
                      rows={3}
                    />
                    <small>{(editFormData.remarks || "").length}/100</small>
                  </div>

                  <div className="form-group">
                    <label>Production Date</label>
                    <input
                      type="date"
                      name="record_date"
                      value={formatDateForInput(editFormData.record_date)}
                      onChange={handleEditFormChange}
                      required
                    />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-submit">
                      Update Record
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to format duration to HH:MM:SS
const formatDuration = (value: string): string => {
  let cleaned = value.replace(/[^\d:]/g, "");
  const parts = cleaned.split(":");

  let hours = parts[0] ? parseInt(parts[0]) || 0 : 0;
  let minutes = parts[1] ? parseInt(parts[1]) || 0 : 0;
  let seconds = parts[2] ? parseInt(parts[2]) || 0 : 0;

  hours = Math.min(Math.max(0, hours), 23);
  minutes = Math.min(Math.max(0, minutes), 59);
  seconds = Math.min(Math.max(0, seconds), 59);

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export default UserDashboard;
